import React, { useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { AuthProvider, useAuth } from './AuthContext';
import { Toaster } from './components/ui/sonner';
import LandingPage from './pages/LandingPage';
import AuthPage from './pages/AuthPage';
import ParentDashboard from './pages/ParentDashboard';
import api from './api';

// Generate or retrieve session ID for visit tracking
const getSessionId = () => {
  let sessionId = sessionStorage.getItem('visit_session_id');
  if (!sessionId) {
    sessionId = 'sess_' + Math.random().toString(36).substring(2, 15) + Date.now().toString(36);
    sessionStorage.setItem('visit_session_id', sessionId);
  }
  return sessionId;
};

// Check if current user is admin or test account
const isAdminOrTestUser = () => {
  try {
    const token = localStorage.getItem('token');
    if (!token) return false;
    
    // Decode JWT to check email (basic check)
    const payload = JSON.parse(atob(token.split('.')[1]));
    const email = payload.sub || payload.email || '';
    
    // Check for admin or test patterns
    const testPatterns = ['admin@', 'test@', 'demo@', '+test', '.test@', 'admin.'];
    return testPatterns.some(pattern => email.toLowerCase().includes(pattern));
  } catch {
    return false;
  }
};

// PageTracker - tracks anonymous page visits for analytics
const PageTracker = () => {
  const location = useLocation();
  
  useEffect(() => {
    // Track page view (fire and forget - don't block)
    const trackPageView = async () => {
      try {
        await api.post('/analytics/page-view', {
          page_path: location.pathname,
          referrer: document.referrer || null,
          session_id: getSessionId(),
          user_agent: navigator.userAgent,
          is_admin_or_test: isAdminOrTestUser()
        });
      } catch (e) {
        // Silently fail - don't disrupt user experience
      }
    };
    
    trackPageView();
  }, [location.pathname]);
  
  return null;
};

// ScrollToTop - ensures every page starts at top on navigation
const ScrollToTop = () => {
  const { pathname } = useLocation();
  
  useEffect(() => {
    // Scroll to top on route change - works for both desktop and mobile
    window.scrollTo(0, 0);
    // Also handle any scrollable containers that might exist
    document.documentElement.scrollTop = 0;
    document.body.scrollTop = 0;
  }, [pathname]);
  
  return null;
};

// Redirect /signup to /auth while preserving query params (for beta invite links)
const SignupRedirect = () => {
  const location = useLocation();
  return <Navigate to={`/auth${location.search}`} replace />;
};
import ChildSelectPage from './pages/ChildSelectPage';
import TemplePage from './pages/TemplePage';
import LessonBuilderPage from './pages/LessonBuilderPage';
import LessonPlayerPage from './pages/LessonPlayerPage';
import TypingDojoPage from './pages/TypingDojoPage';
import LearningPathsPage from './pages/LearningPathsPage';
import CourtyardPage from './pages/CourtyardPage';
import JourneySelectionPage from './pages/JourneySelectionPage';
import ChildProgressPage from './pages/ChildProgressPage';
import RecessPortalPage from './pages/RecessPortalPage';
import TempleSkyPage from './pages/TempleSkyPage';
import AnalyticsDashboardPage from './pages/AnalyticsDashboardPage';
import BillingPage from './pages/BillingPage';
import BillingSuccessPage from './pages/BillingSuccessPage';
import BillingCancelPage from './pages/BillingCancelPage';
import ChallengePage from './pages/ChallengePage';
import './index.css';

// Protected Route Component
const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0F172A] flex items-center justify-center">
        <div className="text-white text-xl font-philosopher">Loading...</div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/auth" replace />;
  }

  return children;
};

// Public Route (redirect if logged in)
const PublicRoute = ({ children }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0F172A] flex items-center justify-center">
        <div className="text-white text-xl font-philosopher">Loading...</div>
      </div>
    );
  }

  if (user) {
    return <Navigate to="/dashboard" replace />;
  }

  return children;
};

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <ScrollToTop />
        <PageTracker />
        <Toaster position="top-center" />
        <Routes>
          {/* Public Routes */}
          <Route path="/" element={<LandingPage />} />
          <Route
            path="/auth"
            element={
              <PublicRoute>
                <AuthPage />
              </PublicRoute>
            }
          />
          {/* Signup redirect - preserves ?code= param for beta invites */}
          <Route path="/signup" element={<SignupRedirect />} />
          
          {/* Challenge Page - Public (no login required) */}
          <Route path="/challenge/:shareCode" element={<ChallengePage />} />

          {/* Protected Routes */}
          <Route
            path="/dashboard"
            element={
              <ProtectedRoute>
                <ParentDashboard />
              </ProtectedRoute>
            }
          />
          <Route
            path="/child-select"
            element={
              <ProtectedRoute>
                <ChildSelectPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/lesson-builder"
            element={
              <ProtectedRoute>
                <LessonBuilderPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/learning-paths"
            element={
              <ProtectedRoute>
                <LearningPathsPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/child-progress/:childId"
            element={
              <ProtectedRoute>
                <ChildProgressPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/analytics"
            element={
              <ProtectedRoute>
                <AnalyticsDashboardPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/billing"
            element={
              <ProtectedRoute>
                <BillingPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/billing/success"
            element={
              <ProtectedRoute>
                <BillingSuccessPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/billing/cancel"
            element={
              <ProtectedRoute>
                <BillingCancelPage />
              </ProtectedRoute>
            }
          />

          {/* Temple and Lesson Routes (require child selection) */}
          <Route path="/temple" element={<TemplePage />} />
          <Route path="/temple-sky" element={<TempleSkyPage />} />
          <Route path="/journey-selection" element={<JourneySelectionPage />} />
          <Route path="/lesson-player/:lessonId" element={<LessonPlayerPage />} />
          <Route path="/typing-dojo" element={<TypingDojoPage />} />
          <Route path="/courtyard" element={<CourtyardPage />} />
          <Route path="/recess" element={<RecessPortalPage />} />
          <Route path="/recess/:roomId" element={<RecessPortalPage />} />

          {/* Fallback */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
